import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { StringKeyValue } from '../actions/types';
import DialogContent from '../components/Common/DialogContent';
import ErrorMessage from '../components/Messages/ErrorMessage';
import { AppState } from '../reducers';
import { ApplicationReducer } from '../reducers/application';
import { CardsReducer } from '../reducers/cards';
import { ContextReducer } from '../reducers/context';
import { LbsReducer } from '../reducers/lbs';
import { LoaderReducer } from '../reducers/loader';

interface CardObject {
    showCardInfo: boolean;
    showCardButtons: boolean;
    showCustomButton: boolean;
    buttonText: string;
    canHaveGold: boolean;
    canHaveBusiness: boolean;
    dialogHeaderTitle: string;
    dialogBodyText: string;
}

interface Props {
    application: ApplicationReducer;
    context: ContextReducer;
    text: StringKeyValue;
    cards: CardsReducer;
    lbs: LbsReducer;
    loader: LoaderReducer;
}

const Dialog = (props: Props) => {
    const navigate = useNavigate();
    const onStepSoknad = () => {
        navigate('/soknad');
    };
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const { context, application, text, cards, lbs, loader } = props;
    const { isValidCustomer } = context;
    const { status } = application;

    const {
        hasGold,
        hasBusiness,
        hasInvalidCards,
        hasAllCardsOtherBank,
        goldCard,
        businessCard,
        canHaveGold,
        canHaveBusiness,
        hasCardOtherBank,
        hasCards,
    } = cards;

    let cardObject: CardObject = {
        showCardInfo: false,
        showCardButtons: false,
        showCustomButton: false,
        buttonText: '',
        canHaveGold,
        canHaveBusiness,
        dialogHeaderTitle: '',
        dialogBodyText: '',
    };

    /* Valid Customer */
    if (isValidCustomer) {
        if (status === 'SOKNAD_PAAGAAENDE' || status === 'UBEHANDLET_BLIKUNDE' || status === 'KONTROLL') {
            /* Customer has an ongoing application */
            cardObject = {
                ...cardObject,
                dialogHeaderTitle: text.newCustomerStopHeader,
                dialogBodyText: text.dialogMessagePendingApplication,
            };
        } else if (hasInvalidCards) {
            /* Customer has Invalid Cards */
            cardObject = {
                ...cardObject,
                dialogBodyText: text.dialogMessageCardUnsupported,
            };
        } else if (hasAllCardsOtherBank) {
            /* Customer has all available Cards in another bank */
            cardObject = {
                ...cardObject,
                dialogBodyText: text.dialogMessageAllCardsOtherBank,
            };
        } else if (
            (!hasGold && canHaveGold && !canHaveBusiness && hasCardOtherBank) ||
            (!hasBusiness && canHaveBusiness && !canHaveGold && hasCardOtherBank)
        ) {
            /* Customer can have only Gold or Business! */
            cardObject = {
                ...cardObject,
                dialogBodyText: text.dialogMessageCardOtherBank,
                showCardButtons: true,
            };
        } else if (hasCards) {
            cardObject = {
                ...cardObject,
                dialogHeaderTitle: text.dialogCardIngress,
                dialogBodyText: text.dialogMessageCard,
                showCardInfo: true,
                showCardButtons: true,
                canHaveGold: hasGold || canHaveGold,
                canHaveBusiness: hasBusiness || canHaveBusiness,
            };
        } else {
            cardObject = {
                ...cardObject,
                dialogBodyText: loader.hasFetchedCards ? text.dialogMessageCard : '',
                showCardInfo: true,
                showCardButtons: true,
                canHaveGold: hasGold || canHaveGold,
                canHaveBusiness: hasBusiness || canHaveBusiness,
            };
        }
    } else {
        /* Invalid Customer! */
        return (
            <ErrorMessage
                title={text.ikkeKundeHeader}
                textList={[text.ikkeKundeText1v2, text.ikkeKundeText3v2]}
                link={text.ikkeKundeLink}
                linkText={text.ikkeKundeLinkText}
                infoBox={text.ikkeKundeInfoBox}
            />
        );
    }
    return (
        <DialogContent
            text={text}
            dialogHeaderTitle={cardObject.dialogHeaderTitle}
            dialogBodyText={cardObject.dialogBodyText}
            showCustomButton={cardObject.showCustomButton}
            showCardButtons={cardObject.showCardButtons}
            buttonText={cardObject.buttonText}
            canHaveGold={cardObject.canHaveGold}
            canHaveBusiness={cardObject.canHaveBusiness}
            hasGold={hasGold}
            hasBusiness={hasBusiness}
            goldCard={goldCard}
            businessCard={businessCard}
            buttonEvent={onStepSoknad}
            lbsbnr={lbs.lbsBankregnr}
            appType={context.appType}
        />
    );
};

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    application: state.application,
    context: state.context,
    cards: state.cards,
    lbs: state.lbs,
    sbl: state.sbl,
    loader: state.loader,
});

export default connect(mapStateToProps)(Dialog);
